import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * Error Boundary specifically designed to handle Google Translate DOM conflicts
 * This catches DOM manipulation errors that occur when Google Translate modifies
 * the DOM structure while React is trying to update components
 */
class GoogleTranslateErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if this is a Google Translate related DOM error
    const isGoogleTranslateError = 
      error.name === 'NotFoundError' && 
      (error.message.includes('insertBefore') || 
       error.message.includes('removeChild') ||
       error.message.includes('The node before which the new node is to be inserted is not a child'));

    if (isGoogleTranslateError) {
      console.warn('Google Translate DOM conflict detected, recovering gracefully:', error.message);
      return { hasError: true, error };
    }

    // For other errors, let them bubble up
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.warn('Google Translate Error Boundary caught an error:', {
      error: error.message,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    // Auto-recover after a short delay
    if (this.state.hasError && !prevState.hasError) {
      setTimeout(() => {
        this.setState({ hasError: false, error: undefined });
      }, 100);
    }
  }

  render() {
    if (this.state.hasError) {
      // Return fallback UI or the original children (for seamless recovery)
      return this.props.fallback || this.props.children;
    }

    return this.props.children;
  }
}

export default GoogleTranslateErrorBoundary;
